(() => {
  const SELECTOR = '.slp-aurora[data-slp-section]';

  function init(root) {
    if (!root || root.dataset.slpInitialized === 'true') return;
    root.dataset.slpInitialized = 'true';

    const stage = root.querySelector('[data-slp-gallery-stage]');
    const slides = Array.from(root.querySelectorAll('[data-slp-media-id]'));
    const thumbnails = Array.from(root.querySelectorAll('[data-slp-thumbnail]'));
    const prev = root.querySelector('[data-slp-prev]');
    const next = root.querySelector('[data-slp-next]');
    const photoCounter = root.querySelector('[data-slp-photo-counter]');
    let activeIndex = Math.max(0, slides.findIndex((slide) => !slide.hidden));

    function updatePhotoCounter() {
      if (photoCounter && slides.length) photoCounter.textContent = `Photo ${activeIndex + 1} of ${slides.length}`;
    }

    function syncPhotoFrame(slide) {
      if (!stage || !slide) return;
      const metadataRatio = Number(slide.dataset.mediaAspectRatio);
      if (metadataRatio > 0) stage.style.setProperty('--slp-media-ratio', String(metadataRatio));
      const image = slide.querySelector('img');
      if (!image) return;
      const fitOriginal = () => {
        if (!slide.hidden && image.naturalWidth && image.naturalHeight) {
          stage.style.setProperty('--slp-media-ratio', String(image.naturalWidth / image.naturalHeight));
        }
      };
      if (image.complete) fitOriginal();
      else image.addEventListener('load', fitOriginal, { once: true });
    }

    function stopInactiveMedia(activeSlide) {
      slides.forEach((slide) => {
        if (slide === activeSlide) return;
        slide.querySelectorAll('video').forEach((video) => video.pause());
        slide.querySelectorAll('iframe').forEach((frame) => {
          if (frame.src) frame.src = frame.src;
        });
      });
    }

    function activateMedia(target) {
      if (!slides.length) return;
      let index = typeof target === 'number'
        ? target
        : slides.findIndex((slide) => slide.dataset.slpMediaId === String(target));

      if (index < 0) return;
      activeIndex = (index + slides.length) % slides.length;

      slides.forEach((slide, slideIndex) => {
        const isActive = slideIndex === activeIndex;
        slide.hidden = !isActive;
        slide.setAttribute('aria-hidden', String(!isActive));
      });

      thumbnails.forEach((thumbnail, thumbnailIndex) => {
        const isActive = thumbnailIndex === activeIndex;
        thumbnail.setAttribute('aria-current', String(isActive));
        if (isActive && thumbnail.parentElement) thumbnail.parentElement.scrollTo({ left: Math.max(0, thumbnail.offsetLeft - thumbnail.parentElement.clientWidth / 2 + thumbnail.clientWidth / 2), behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
      });

      syncPhotoFrame(slides[activeIndex]);
      updatePhotoCounter();
      stopInactiveMedia(slides[activeIndex]);
    }

    thumbnails.forEach((thumbnail, index) => {
      thumbnail.addEventListener('click', () => activateMedia(index));
    });

    prev?.addEventListener('click', () => activateMedia(activeIndex - 1));
    next?.addEventListener('click', () => activateMedia(activeIndex + 1));

    stage?.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        activateMedia(activeIndex - 1);
      }
      if (event.key === 'ArrowRight') {
        event.preventDefault();
        activateMedia(activeIndex + 1);
      }
      if (event.key === 'Home' || event.key === 'End') {
        event.preventDefault();
        activateMedia(event.key === 'Home' ? 0 : slides.length - 1);
      }
    });

    if (stage && slides.length > 1) {
      let pointerStart = null;
      stage.addEventListener('pointerdown', (event) => {
        if (event.isPrimary === false || event.target.closest('video, iframe, model-viewer, button')) return;
        pointerStart = { x: event.clientX, y: event.clientY, id: event.pointerId };
      });
      stage.addEventListener('pointerup', (event) => {
        if (pointerStart === null || pointerStart.id !== event.pointerId) return;
        const delta = event.clientX - pointerStart.x;
        const vertical = event.clientY - pointerStart.y;
        pointerStart = null;
        if (Math.abs(delta) < 48 || Math.abs(delta) < Math.abs(vertical) * 1.3) return;
        activateMedia(activeIndex + (delta < 0 ? 1 : -1));
      });
      stage.addEventListener('dragstart', event => event.preventDefault());
      stage.addEventListener('pointercancel', () => { pointerStart = null; });
    }

    const variantSelector = root.querySelector('[data-slp-variant-select]');
    const variantInput = root.querySelector('[data-slp-variant-id]');
    const variantButtons = Array.from(root.querySelectorAll('[data-slp-variant-button]'));
    const stock = root.querySelector('[data-slp-stock]');
    const savings = root.querySelector('[data-slp-savings]');
    const discountNote = root.querySelector('[data-slp-discount-note]');
    const discountBadge = root.querySelector('[data-slp-discount-badge]');
    const regularRow = root.querySelector('[data-slp-regular-row]');
    const offerFooter = root.querySelector('[data-slp-offer-footer]');
    const priceLabel = root.querySelector('[data-slp-price-label]');
    const dealCopy = root.querySelector('[data-slp-deal-copy]');
    const valueSubheading = root.querySelector('[data-slp-value-subheading]');
    const price = root.querySelector('[data-slp-price]');
    const comparePrice = root.querySelector('[data-slp-compare-price]');
    const addButton = root.querySelector('[data-slp-add-button]');
    const isPrelaunch = root.dataset.slpPrelaunch === 'true';

    function updateVariant() {
      if (!variantSelector) return;
      const option = variantSelector.options[variantSelector.selectedIndex];
      if (!option) return;

      if (variantInput) variantInput.value = option.value;
      if (price) price.textContent = option.dataset.price || price.textContent;

      if (comparePrice) {
        comparePrice.textContent = option.dataset.comparePrice || '';
        comparePrice.hidden = !option.dataset.comparePrice;
      }

      if (addButton) {
        const available = option.dataset.available === 'true';
        addButton.disabled = isPrelaunch || !available;
        addButton.type = addButton.disabled ? 'button' : 'submit';
        addButton.textContent = isPrelaunch
          ? addButton.dataset.prelaunchLabel
          : available
            ? `${addButton.dataset.liveLabel} — ${option.dataset.price}`
            : addButton.dataset.soldoutLabel;
      }

      variantButtons.forEach(button => {
        const active = button.dataset.variantId === option.value;
        button.classList.toggle('is-active', active);
        button.setAttribute('aria-pressed', String(active));
      });
      if (stock) stock.textContent = option.dataset.available === 'true' ? 'Available now' : 'Sold out';
      if (savings) { savings.textContent = option.dataset.savings || ''; savings.hidden = !option.dataset.savings; }
      if (discountBadge) discountBadge.hidden = !option.dataset.savings;
      if (regularRow) regularRow.hidden = !option.dataset.comparePrice;
      if (offerFooter) offerFooter.hidden = !option.dataset.savings;
      if (priceLabel) priceLabel.textContent = option.dataset.comparePrice ? 'Now' : 'Price';
      if (discountNote) discountNote.hidden = option.dataset.discountLite !== 'true';
      if (dealCopy) { dealCopy.textContent = option.dataset.dealCopy || ''; dealCopy.hidden = !option.dataset.dealCopy; }
      if (valueSubheading) valueSubheading.textContent = option.dataset.valueSubheading || 'Fully built. Ready for your setup.';
      const selectedColor = root.querySelector('[data-slp-selected-color]');
      if (selectedColor) selectedColor.textContent = option.textContent.replace(/\s+— Sold out$/, '');
      if (option.dataset.mediaId) activateMedia(option.dataset.mediaId);
      updateStickyPurchase();

      const url = new URL(window.location.href);
      url.searchParams.set('variant', option.value);
      window.history.replaceState({}, '', url);
    }

    variantSelector?.addEventListener('change', updateVariant);
    variantButtons.forEach(button => button.addEventListener('click', () => {
      if (!variantSelector) return;
      variantSelector.value = button.dataset.variantId;
      updateVariant();
    }));
    if (variantSelector && variantButtons.length) {
      variantSelector.tabIndex = -1;
      variantSelector.setAttribute('aria-hidden', 'true');
    }
    variantButtons.forEach((button, index) => button.addEventListener('keydown', event => {
      const offset = { ArrowRight: 1, ArrowLeft: -1, ArrowDown: 2, ArrowUp: -2 }[event.key];
      if (offset === undefined) return;
      event.preventDefault();
      variantButtons[(index + offset + variantButtons.length) % variantButtons.length]?.focus();
    }));
    root.classList.add('slp-enhanced');
    syncPhotoFrame(slides[activeIndex]);
    updatePhotoCounter();

    const stickyPurchase = root.querySelector('[data-slp-sticky-purchase]');
    const returnToBuy = root.querySelector('[data-slp-return-to-buy]');
    const mobileQuery = window.matchMedia('(max-width: 989px)');

    function updateStickyPurchase() {
      if (!stickyPurchase || !addButton) return;
      const option = variantSelector?.options[variantSelector.selectedIndex];
      const stickyPrice = stickyPurchase.querySelector('[data-slp-sticky-price]');
      const stickyTitle = stickyPurchase.querySelector('[data-slp-sticky-title]');
      if (stickyPrice) stickyPrice.textContent = option?.dataset.price || price?.textContent || '';
      if (stickyTitle && option) stickyTitle.textContent = option.textContent.replace(/\s+— Sold out$/, '');
      if (returnToBuy) {
        returnToBuy.textContent = option?.dataset.available === 'false' ? 'Choose another color' : 'Choose your color';
      }
      const primary = addButton.getBoundingClientRect();
      const sectionBounds = root.getBoundingClientRect();
      const primaryVisible = primary.bottom > 0 && primary.top < window.innerHeight;
      const sectionVisible = sectionBounds.bottom > 86 && sectionBounds.top < window.innerHeight;
      const productEndVisible = sectionBounds.bottom < window.innerHeight - 40;
      const visible = mobileQuery.matches && sectionVisible && !productEndVisible && !primaryVisible && !isPrelaunch;
      stickyPurchase.hidden = !visible;
      root.classList.toggle('slp-sticky-visible', visible);
    }

    returnToBuy?.addEventListener('click', () => {
      const picker = root.querySelector('.slp-variant-picker') || addButton;
      picker?.scrollIntoView({ behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'start' });
      root.querySelector('.slp-variant-option.is-active')?.focus({ preventScroll: true });
    });

    if (stickyPurchase) {
      window.addEventListener('scroll', updateStickyPurchase, { passive: true });
      window.addEventListener('resize', updateStickyPurchase, { passive: true });
      mobileQuery.addEventListener?.('change', updateStickyPurchase);
      if ('IntersectionObserver' in window) {
        const observer = new IntersectionObserver(updateStickyPurchase, { threshold: [0, 1] });
        observer.observe(addButton);
        observer.observe(root);
      }
      updateStickyPurchase();
    }

    const quantityInput = root.querySelector('[data-slp-quantity]');
    root.querySelectorAll('[data-slp-quantity-change]').forEach((button) => {
      button.addEventListener('click', () => {
        if (!quantityInput) return;
        const delta = Number(button.dataset.slpQuantityChange || 0);
        const min = Number(quantityInput.min || 1);
        const max = quantityInput.max ? Number(quantityInput.max) : Infinity;
        const current = Number(quantityInput.value || min);
        quantityInput.value = String(Math.min(max, Math.max(min, current + delta)));
        quantityInput.dispatchEvent(new Event('change', { bubbles: true }));
      });
    });

    quantityInput?.addEventListener('change', () => {
      const min = Number(quantityInput.min || 1);
      const max = quantityInput.max ? Number(quantityInput.max) : Infinity;
      const current = Number(quantityInput.value || min);
      quantityInput.value = String(Math.min(max, Math.max(min, Number.isFinite(current) ? current : min)));
    });

    const countdown = root.querySelector('[data-slp-countdown]');
    if (countdown && isPrelaunch) {
      const releaseAt = Date.parse(countdown.dataset.releaseAt || '');
      const fields = {
        days: countdown.querySelector('[data-slp-days]'),
        hours: countdown.querySelector('[data-slp-hours]'),
        minutes: countdown.querySelector('[data-slp-minutes]'),
        seconds: countdown.querySelector('[data-slp-seconds]')
      };
      let reloading = false;

      function updateCountdown() {
        if (!Number.isFinite(releaseAt)) return;
        const remaining = releaseAt - Date.now();
        if (remaining <= 0) {
          Object.values(fields).forEach((field) => { if (field) field.textContent = '00'; });
          if (!reloading) {
            reloading = true;
            window.setTimeout(() => window.location.reload(), 750);
          }
          return;
        }

        const days = Math.floor(remaining / 86400000);
        const hours = Math.floor((remaining % 86400000) / 3600000);
        const minutes = Math.floor((remaining % 3600000) / 60000);
        const seconds = Math.floor((remaining % 60000) / 1000);
        if (fields.days) fields.days.textContent = String(days).padStart(2, '0');
        if (fields.hours) fields.hours.textContent = String(hours).padStart(2, '0');
        if (fields.minutes) fields.minutes.textContent = String(minutes).padStart(2, '0');
        if (fields.seconds) fields.seconds.textContent = String(seconds).padStart(2, '0');
      }

      updateCountdown();
      window.setInterval(updateCountdown, 1000);
    }
  }

  function initAll(scope = document) {
    scope.querySelectorAll(SELECTOR).forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll());
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
})();
